import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Loading from '../Components/Loading';
import { useNavigate } from 'react-router-dom';

import { API_URL } from '../config/api';

const sortFieldMap = {
    'name': 'FirstName',
    'firstName': 'FirstName',
    'lastName': 'LastName',
    'email': 'Email',
    'mobileNumber': 'MobileNumber',
    'phoneNumber': 'MobileNumber',
    'designation': 'Designation',
    'employmentStatus': 'EmployeeStatus',
    'dateOfBirth': 'DateofBirth',
    'id': '_id',
    'createdAt': 'createdAt',
    'updatedAt': 'updatedAt',
    'employeeId': 'employeeid', 
};

const Employee = () => {
    const navigate = useNavigate();
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 25,
        hasNextPage: false,
        hasPreviousPage: false
    });
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [sortBy, setSortBy] = useState('');
    const [sortOrder, setSortOrder] = useState('asc');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [employeeToDelete, setEmployeeToDelete] = useState(null);
    const searchTimeoutRef = useRef(null);

    // Debounce search: wait 500ms after user stops typing before triggering API
    useEffect(() => {
        // Clear existing timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Set new timeout - only trigger API if search is empty or has at least 2 characters
        if (search === '' || search.trim().length >= 2) {
            searchTimeoutRef.current = setTimeout(() => {
                setDebouncedSearch(search);
            }, 500); // 500ms delay after user stops typing
        } else {
            // If less than 2 characters, clear the debounced search
            setDebouncedSearch('');
        }

        // Cleanup timeout on unmount or when search changes
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [search]);

    // Reset to page 1 when debounced search changes
    useEffect(() => {
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    }, [debouncedSearch]);

    // Fetch employees when debounced search or other filters change
    useEffect(() => {
        fetchEmployees();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.currentPage, pagination.limit, debouncedSearch, sortBy, sortOrder]);

    const fetchEmployees = async () => {
        if (!API_URL) {
            toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
            return;
        }

        setLoading(true);
        try {
            // Map frontend field name to backend field name
            const mappedSortBy = sortBy && sortFieldMap[sortBy] ? sortFieldMap[sortBy] : sortBy;

            const payload = {
                action: 'get-employees',
                search: debouncedSearch,
                page: pagination.currentPage,
                limit: pagination.limit,
                sortBy: mappedSortBy,
                sortOrder: sortOrder
            };

            const response = await axios.post(API_URL, payload, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const result = response.data;

            if (result?.success) {
                setEmployees(result.employees || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: result.pagination?.currentPage || prev.currentPage,
                    totalPages: result.pagination?.totalPages || prev.totalPages,
                    totalCount: result.pagination?.totalCount || prev.totalCount,
                    hasNextPage: result.pagination?.hasNextPage || false,
                    hasPreviousPage: result.pagination?.hasPreviousPage || false
                }));
            } else {
                toast.error(result?.message || 'Failed to fetch employees');
            }
        } catch (error) {
            console.error('Error fetching employees:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            const day = date.getDate();
            const month = date.toLocaleString('default', { month: 'short' });
            const year = date.getFullYear();
            return `${day}-${month}-${year}`;
        } catch (error) {
            return dateString;
        }
    };


    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, currentPage: newPage }));
        }
    };

    const handleLimitChange = (e) => {
        const newLimit = parseInt(e.target.value);
        setPagination(prev => ({ ...prev, limit: newLimit, currentPage: 1 }));
    };

    const handleSearch = (e) => {
        const value = e.target.value;
        setSearch(value); // Update input immediately for good UX
        // Debouncing and pagination reset is handled in useEffect
    };

    const handleSort = (field) => {
        if (sortBy === field) {
            // Toggle sort order if clicking the same field
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            // Set new sort field and default to ascending
            setSortBy(field);
            setSortOrder('asc');
        }
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    const getSerialNumber = (index) => {
        return (pagination.currentPage - 1) * pagination.limit + index + 1;
    };

    const handleView = (employee) => {
        if (employee?.id) {
            navigate(`/employeeview/${employee.id}`);
        } else {
            toast.error('Unable to open employee. Missing identifier.');
        }
    };

    const handleEdit = (employee) => {
        if (employee?.id) {
            navigate(`/employeeedit/${employee.id}`);
        } else {
            toast.error('Unable to open employee. Missing identifier.');
        }
    };

    const handleDelete = (employee) => {
        setEmployeeToDelete(employee);
        setShowDeleteModal(true);
    };

    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setEmployeeToDelete(null);
    };

    const confirmDelete = async () => {
        if (!employeeToDelete) return;

        const updatedBy = localStorage.getItem('id');
        
        if (!updatedBy) {
            toast.error('User ID not found. Please login again.');
            closeDeleteModal();
            return;
        }

        setLoading(true);
        try {
            const payload = {
                action: 'delete-employee',
                id: employeeToDelete.id,
                updatedBy: updatedBy
            };

            const response = await axios.post(API_URL, payload, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const result = response.data;

            if (result?.success) {
                toast.success(result?.message || 'Employee deleted successfully');
                closeDeleteModal();
                fetchEmployees();
            } else {
                toast.error(result?.message || 'Failed to delete employee');
            }
        } catch (error) {
            console.error('Error deleting employee:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="employee-form">
            <section className="content">
                <div className="container-fluid">
                    {/* Header with Title */}
                    <div className="row mb-3 employee-header-row">
                        <div className="col-md-12">
                            <h1 className="employee-title">
                                Manage Employee
                                <i className="fas fa-info-circle ml-2 employee-title-icon"></i>
                            </h1>
                            <p className="employee-description">
                                View, edit, and manage employee information and details.
                            </p>
                        </div>
                    </div>

                    {/* Employee Table Card */}
                    <div className="card employee-content-card">
                        <div className="card-body">
                            <div className="employee-table-container">
                                <div className="employee-search-container">
                                    <div className="form-group">
                                        <input
                                            type="text"
                                            className="form-input employee-search-input"
                                            placeholder="Search employees..."
                                            value={search}
                                            onChange={handleSearch}
                                        />
                                    </div>
                                </div>

                                {loading ? (
                                    <Loading size={120} />
                                ) : (
                                    <>
                                        <div className="employee-table-wrapper">
                                            <table className="employee-table">
                                                <thead>
                                                    <tr>
                                                        <th className="employee-sortable">SI No.</th>
                                                         <th className="employee-sortable"
                                                            onClick={() => handleSort("employeeId")}>
                                                            Employee ID
                                                            {sortBy === "employeeId" && (
                                                            <i
                                                              className={`fas fa-sort-${sortOrder === "asc" ? "up" : "down"} ml-1`}></i>
                                                              )}
                                                             {sortBy !== "employeeId" && (
                                                               <i className="fas fa-sort ml-1 employee-sort-icon"></i>
                                                             )}
                                                        </th>
                                                        <th
                                                            className="employee-sortable"
                                                            onClick={() => handleSort('name')}
                                                        >
                                                            Name
                                                            {sortBy === 'name' && (
                                                                <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ml-1`}></i>
                                                            )}
                                                            {sortBy !== 'name' && (
                                                                <i className="fas fa-sort ml-1 employee-sort-icon"></i>
                                                            )}
                                                        </th>
                                                        <th
                                                            className="employee-sortable"
                                                            onClick={() => handleSort('email')}
                                                        >
                                                            Email
                                                            {sortBy === 'email' && (
                                                                <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ml-1`}></i>
                                                            )}
                                                            {sortBy !== 'email' && (
                                                                <i className="fas fa-sort ml-1 employee-sort-icon"></i>
                                                            )}
                                                        </th>
                                                        <th>Date of Birth</th>
                                                        <th>Mobile</th>
                                                        <th
                                                            className="employee-sortable"
                                                            onClick={() => handleSort('designation')}
                                                        >
                                                            Designation
                                                            {sortBy === 'designation' && (
                                                                <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ml-1`}></i>
                                                            )}
                                                            {sortBy !== 'designation' && (
                                                                <i className="fas fa-sort ml-1 employee-sort-icon"></i>
                                                            )}
                                                        </th>
                                                        <th
                                                            className="employee-sortable"
                                                            onClick={() => handleSort('employmentStatus')}
                                                        >
                                                            Status
                                                            {sortBy === 'employmentStatus' && (
                                                                <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ml-1`}></i>
                                                            )}
                                                            {sortBy !== 'employmentStatus' && (
                                                                <i className="fas fa-sort ml-1 employee-sort-icon"></i>
                                                            )}
                                                        </th>
                                                        <th>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {employees.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="8" className="employee-empty">
                                                                No employees found
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        employees.map((employee, index) => {
                                                            return (
                                                                <tr key={employee.id}>
                                                                    <td>{getSerialNumber(index)}</td>
                                                                    <td>{employee.employeeId || "-"}</td>
                                                                    <td>{employee.name || '-'}</td>
                                                                    <td>{employee.email || '-'}</td>
                                                                    <td>{formatDate(employee.dateOfBirth)}</td>
                                                                    <td>{employee.mobileNumber || '-'}</td>
                                                                    <td>{employee.designationName || '-'}</td>
                                                                    <td>
                                                                        <span className={`employee-status employee-status-${employee.employmentStatus?.toLowerCase() || 'active'}`}>
                                                                            {employee.employmentStatus?.toUpperCase() || 'ACTIVE'}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        <div className="employee-actions">
                                                                            <button
                                                                                className="employee-action-btn employee-action-view"
                                                                                onClick={() => handleView(employee)}
                                                                                title="View"
                                                                            >
                                                                                <i className="fa-solid fa-eye"></i>
                                                                            </button>
                                                                            <button
                                                                                className="employee-action-btn employee-action-edit"
                                                                                onClick={() => handleEdit(employee)}
                                                                                title="Edit"
                                                                            >
                                                                                <i className="fa-solid fa-pencil"></i>
                                                                            </button>
                                                                            <button
                                                                                className="employee-action-btn employee-action-delete"
                                                                                onClick={() => handleDelete(employee)}
                                                                                title="Delete"
                                                                            >
                                                                                <i className="fa-solid fa-trash"></i>
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>

                                        {employees.length > 0 && pagination.totalPages > 0 && (
                                            <div className="employee-pagination">
                                                <div className="pagination-limit-container" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginRight: 'auto' }}>
                                                    <label htmlFor="paginationLimit" style={{ margin: 0, fontSize: '14px', color: '#495057' }}>
                                                        Show:
                                                    </label>
                                                    <select
                                                        id="paginationLimit"
                                                        value={pagination.limit}
                                                        onChange={handleLimitChange}
                                                        style={{
                                                            padding: '6px 10px',
                                                            fontSize: '14px',
                                                            border: '1px solid #ced4da',
                                                            borderRadius: '4px',
                                                            backgroundColor: 'white',
                                                            cursor: 'pointer',
                                                            minWidth: '70px'
                                                        }}
                                                    >
                                                        <option value="25">25</option>
                                                        <option value="50">50</option>
                                                        <option value="75">75</option>
                                                        <option value="100">100</option>
                                                    </select>
                                                </div>
                                                <div style={{ display: 'flex', gap: '5px' }}>
                                                    <button
                                                        className="pagination-btn"
                                                        onClick={() => handlePageChange(1)}
                                                        disabled={pagination.currentPage === 1}
                                                    >
                                                        «
                                                    </button>
                                                    <button
                                                        className="pagination-btn"
                                                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                                                        disabled={pagination.currentPage === 1}
                                                    >
                                                        ‹
                                                    </button>
                                                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                                                        <button
                                                            key={page}
                                                            className={`pagination-btn ${pagination.currentPage === page ? 'active' : ''}`}
                                                            onClick={() => handlePageChange(page)}
                                                        >
                                                            {page}
                                                        </button>
                                                    ))}
                                                    <button
                                                        className="pagination-btn"
                                                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                                                        disabled={pagination.currentPage === pagination.totalPages}
                                                    >
                                                        ›
                                                    </button>
                                                    <button
                                                        className="pagination-btn"
                                                        onClick={() => handlePageChange(pagination.totalPages)}
                                                        disabled={pagination.currentPage === pagination.totalPages}
                                                    >
                                                        »
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Delete Confirmation Modal */}
           {showDeleteModal && (
                <div className="modal-overlay" onClick={closeDeleteModal}>
                    <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Confirm Delete</h3>
                            <button className="modal-close" onClick={closeDeleteModal}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>Are you sure you want to delete <strong>{employeeToDelete?.name}</strong>?</p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-primary" onClick={confirmDelete}>
                                Yes
                            </button>
                        </div>
                    </div>
                </div>
            )}
          </div>
    );
};

export default Employee;
